import { BlankScreen } from '@strata/react-page-templates';
import Holidays from 'date-holidays';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { type FC, type PropsWithChildren, type ReactElement, useEffect, useState } from 'react';

import { HolidayScreen } from 'components/liveReferrals/offHoursScreens/HolidayScreen';
import { ObservedHolidays } from 'components/liveReferrals/offHoursScreens/ObservedHolidays';
import { OffHoursScreen } from 'components/liveReferrals/offHoursScreens/OffHoursScreen';
import { type LiveReferralHours, useGetLiveReferralHoursQuery } from 'store/api/auctionApi';

dayjs.extend(utc);
dayjs.extend(timezone);

const holidays = new Holidays('US');

/**
 * Return off hours or holiday component
 */
const isOffHoursOrHoliday = (liveReferralHours?: LiveReferralHours) => {
    const currDate = dayjs().tz('America/New_York');
    const currDay = currDate.day();
    const currHour = currDate.hour();

    const currentHolidays = holidays.isHoliday(currDate.toDate());

    if (!liveReferralHours) {
        return null;
    }

    const currentHoliday = Array.isArray(currentHolidays) ? currentHolidays?.[0] : undefined;
    if (currentHoliday && Object.values<string>(ObservedHolidays).includes(currentHoliday.name)) {
        return <HolidayScreen holidayName={currentHoliday.name} />;
    }

    const { start, end } = liveReferralHours;
    if (currDay < 1 || currDay > 5) {
        return <OffHoursScreen type={OffHoursScreen.Types.WEEKEND} />;
    }
    if (currHour < start) {
        return <OffHoursScreen type={OffHoursScreen.Types.OPENING} />;
    }
    if (currHour >= end) {
        return <OffHoursScreen type={OffHoursScreen.Types.CLOSED} />;
    }

    return null;
};

export const OffHoursGuard: FC<PropsWithChildren> = ({ children = undefined }) => {
    const { data: liveReferralHours, isLoading } = useGetLiveReferralHoursQuery();

    const [OffHoursComponent, setOffHoursComponent] = useState(
        isOffHoursOrHoliday(liveReferralHours),
    );
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setOffHoursComponent(isOffHoursOrHoliday(liveReferralHours));
        const interval = setInterval(() => {
            setOffHoursComponent(isOffHoursOrHoliday(liveReferralHours));
        }, 1000);
        return () => {
            clearInterval(interval);
        };
    }, [liveReferralHours]);

    if (isLoading) {
        return <BlankScreen />;
    }

    return OffHoursComponent ?? (children as ReactElement);
};
